import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  EmployeePayrollHistoryArgs,
  EmployeePayrollTotalsArgs,
  ReportDownloadArgs,
  ReportGeneratedArgs
} from '../../../../models/reports';
import moment from 'moment';
import {
  formatDatePickerValue,
  parseDatePickerValue
} from '../../../../utils/datepicker-value-parser';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import {
  dateRangeComponentValidator,
  dateRangeRequired
} from '../../../../utils/dateRangeValidator';
import { PeopleService } from '../../../../services/people.service';
import { Person } from '../../../../models/person';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { PagedPayload } from '../../../../models/paged-payload';
import { Option } from '../../../../components/multiselect/multiselect.component';

@Component({
  selector: 'lib-employee-payroll-history',
  templateUrl: './employee-payroll-history.component.html',
  styleUrls: ['./employee-payroll-history.component.css'],
  standalone: false
})
export class EmployeePayrollHistoryComponent implements OnInit {
  @Input()
  public clientCode: string;
  @Output()
  public reportGenerated = new EventEmitter<ReportGeneratedArgs>();
  @Output()
  public reportDownloadClicked = new EventEmitter<ReportDownloadArgs>();
  public peopleObs: BehaviorSubject<Option[]>;
  public people: Person[] = [];
  private readonly ENDPOINT = 'EmployeePayrollHistory';
  public reportGenerationForm: UntypedFormGroup;
  constructor(
    private fb: UntypedFormBuilder,
    private peopleService: PeopleService
  ) {}

  ngOnInit(): void {
    this.peopleObs = new BehaviorSubject<Option[]>([]);
    this.peopleService
      .searchPeople(this.clientCode, { pageNumber: 1, perPage: 100 })
      .subscribe(result => {
        this.people.push(...result.Items);
        if (result.Pagination.Pages > 1) {
          const obs: Observable<PagedPayload<Person>>[] = [];
          for (let i = 2; i <= result.Pagination.Pages; i++) {
            obs.push(
              this.peopleService.searchPeople(this.clientCode, { pageNumber: i, perPage: 100 })
            );
          }
          forkJoin(obs).subscribe((additionalResults: PagedPayload<Person>[]) => {
            additionalResults.forEach(x => this.people.push(...x.Items));
            this.peopleObs.next(this.people.map(this._convertPersonToOption));
            this._initForm();
          });
        } else {
          this.peopleObs.next(this.people.map(this._convertPersonToOption));
          this._initForm();
        }
      });
  }
  public generateReport() {
    const args = this._getModel();
    this.reportGenerated.emit({ args, endpoint: this.ENDPOINT });
  }

  public downloadReport(format: string) {
    const args = this._getModel();
    const formattedStartDate = moment(args.StartDate).format('MM-DD-YYYY');
    const formattedEndDate = moment(args.EndDate).format('MM-DD-YYYY');
    const now = moment().format('MMDDYYYY');
    this.reportDownloadClicked.emit({
      args,
      endpoint: this.ENDPOINT,
      filename: `Employee Payroll History_${this.clientCode}_${formattedStartDate} to ${formattedEndDate}_${now}.${format}`,
      format
    });
  }
  private _convertPersonToOption(person: Person): Option {
    let middleInitial = '';
    if (person.MiddleName != null && person.MiddleName != '') {
      middleInitial = ` ${person.MiddleName[0]}`;
    }
    return {
      value: person.Id,
      text: `${person.LastName}, ${person.FirstName}${middleInitial}`
    };
  }
  private _getModel() {
    const formValue = this.reportGenerationForm.get('dateRange').value;
    const args: EmployeePayrollHistoryArgs = {
      StartDate: formValue.start,
      EndDate: formValue.end,
      IncludePickupFigures: this.reportGenerationForm.get('includePickupOrSuccessorFigures').value,
      ReportPeriodTotals: this.reportGenerationForm.get('reportPeriodTotals').value,
      EmployeeIds: this.reportGenerationForm.get('employeeIds').value
    };
    return args;
  }
  private _initForm() {
    const now = new Date();
    const start = formatDatePickerValue(new Date(now.getFullYear(), 0, 1));
    const end = formatDatePickerValue(now);
    this.reportGenerationForm = this.fb.group(
      {
        limitEmployees: this.fb.control(false),
        employeeIds: this.fb.control([]),
        dateRange: this.fb.control({ start: null, end: null }, [
          dateRangeRequired,
          dateRangeComponentValidator
        ]),
        includePickupOrSuccessorFigures: this.fb.control(false),
        reportPeriodTotals: this.fb.control(false)
      },
      { validators: [MinimumOneEmployee] }
    );
    this.reportGenerationForm.get('limitEmployees').valueChanges.subscribe(val => {
      if (val) {
        this.peopleObs.next(
          this.people
            .filter((x: any) => {
              const date = new Date(Date.parse(x.LastPaid));
              const now = new Date();
              const lastYear = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
              return date > lastYear;
            })
            .map(this._convertPersonToOption)
        );
      } else {
        this.peopleObs.next(this.people.map(this._convertPersonToOption));
      }
      this.reportGenerationForm.get('employeeIds').setValue([]);
    });
  }
}
export const MinimumOneEmployee: ValidatorFn = (form: AbstractControl) => {
  const employeeIds = form.get('employeeIds').value;
  if (employeeIds == null || employeeIds.length == 0) return { minimumOne: true };
  return null;
};
